import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, startTransition } from 'react';
import axios from 'axios';
import { 
  Search, 
  Play, 
  Square, 
  AlertCircle,
  Image,
  Clock,
  TrendingUp,
  RefreshCw
} from 'lucide-react';

// Module-level flags to prevent multiple initializations across component instances
// NEVER reset these during the session - they persist until page reload
let searchPageInitInProgress = false; // Track if initialization is in progress
let stolenImagesFetched = false; // Track if stolen images have been fetched (persists across visits)

const SearchPage = ({ searchStatus, setSearchStatus, showNotification }) => {
  console.log('[SearchPage] Component rendering - this log should help track remounts');
  
  const [maxPages, setMaxPages] = useState(5);
  const [detectedTotalPages, setDetectedTotalPages] = useState(null);
  const [detectedTotalResults, setDetectedTotalResults] = useState(null);
  const [detectingTotal, setDetectingTotal] = useState(false);
  const [initDone, setInitDone] = useState(false);
  const [stolenImages, setStolenImages] = useState([]);
  const [stolenImagesLoading, setStolenImagesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [scrapedImages, setScrapedImages] = useState([]);
  const [compareMode, setCompareMode] = useState('auto'); // Always use 'auto' mode
  const [showBusyModal, setShowBusyModal] = useState(false);
  
  // Pagination state
  const [scrapedImagesOffset, setScrapedImagesOffset] = useState(0);
  const [scrapedImagesHasMore, setScrapedImagesHasMore] = useState(true);
  const [scrapedImagesLoading, setScrapedImagesLoading] = useState(false);
  const initRef = useRef(false);
  const detectingTotalRef = useRef(false);
  const initRefLocal = useRef(false);
  const activeRunRef = useRef(false); // Only show realtime logs for runs started in this session
  const scrapedImagesScrollRef = useRef(null);

  // API fetch functions - defined early so they can be used in useEffect hooks
  const fetchSearchStatus = useCallback(async () => {
    try {
      const response = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/search/status');
      if (response.data) {
        setSearchStatus(response.data);
        if (response.data.mode) {
          setCompareMode(response.data.mode);
        }
      }
    } catch (error) {
      console.error('[SearchPage] Error fetching search status:', error);
    }
  }, [setSearchStatus]);

  const fetchStolenImages = useCallback(async (forceRefresh = false) => {
    // Skip if already fetched and not forcing refresh
    if (stolenImagesFetched && !forceRefresh) {
      console.log('[SearchPage] Skipping stolen images fetch - already fetched');
      return;
    }
    
    try {
      setStolenImagesLoading(true);
      const response = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/stolen-images');
      const images = response.data?.images || [];
      const imagesArray = Array.isArray(images) ? images : [];
      setStolenImages(imagesArray);
      stolenImagesFetched = true; // Mark as fetched
    } catch (error) {
      console.error('[SearchPage] Error fetching stolen images:', error);
      setStolenImages([]);
      showNotification('Error loading stolen images', 'error');
    } finally {
      setStolenImagesLoading(false);
    }
  }, [showNotification]);

  // Poll for search status updates when search is running
  useEffect(() => {
    let pollInterval = null;
    
    if (searchStatus.is_running) {
      // Poll every 2 seconds when search is running
      pollInterval = setInterval(() => {
        fetchSearchStatus();
      }, 2000);
    }
    
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [searchStatus.is_running, fetchSearchStatus]);

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/statistics');
      const statsData = response.data || {};
      setStats(statsData);
    } catch (error) {
      console.error('[SearchPage] Error fetching statistics:', error);
      setStats({});
    }
  }, []);

  const detectTotalPages = useCallback(async (forceRefresh = false) => {
    if (detectingTotalRef.current) {
      return;
    }
    
    const hasStateValue = detectedTotalPages !== null && detectedTotalPages !== undefined;
    if (hasStateValue && !forceRefresh) {
      return;
    }
    
    // Set loading state immediately before API call
    detectingTotalRef.current = true;
    setDetectingTotal(true);
    
    try {
      const resp = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/ebay/total', {
        timeout: 30000
      });
      const data = resp?.data || {};
      
      if (data.success && data.total_pages !== null && data.total_pages !== undefined) {
        setDetectedTotalPages(data.total_pages);
        setDetectedTotalResults(data.total_results || null);
      }
    } catch (e) {
      console.error('[SearchPage] Could not detect total pages:', e.message);
    } finally {
      detectingTotalRef.current = false;
      setDetectingTotal(false);
    }
  }, [detectedTotalPages]);

  const fetchScrapedImages = useCallback(async (append = false) => {
    if (scrapedImagesLoading || (!append && !scrapedImagesHasMore)) return;
    
    try {
      setScrapedImagesLoading(true);
      const offset = append ? scrapedImagesOffset : 0;
      const response = await axios.get(`https://outiloeuvresvolees.inouiagency.com/api/scraped-images?limit=20&offset=${offset}`);
      if (response.data.success) {
        const images = response.data.images || [];
        const imagesArray = Array.isArray(images) ? images : [];
        
        if (append) {
          setScrapedImages(prev => [...prev, ...imagesArray]);
          setScrapedImagesOffset(prev => prev + imagesArray.length);
        } else {
          setScrapedImages(imagesArray);
          setScrapedImagesOffset(imagesArray.length);
        }
        
        setScrapedImagesHasMore(response.data.has_more || false);
      } else {
        if (!append) {
          setScrapedImages([]);
        }
      }
    } catch (error) {
      console.error('[SearchPage] Error fetching scraped images:', error);
      if (!append) {
        setScrapedImages([]);
      }
    } finally {
      setScrapedImagesLoading(false);
    }
  }, [scrapedImagesOffset, scrapedImagesHasMore, scrapedImagesLoading]);

  // Scroll handlers for infinite scroll
  const handleScrapedImagesScroll = useCallback((e) => {
    const container = e.target;
    const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    
    // Load more when within 200px of bottom
    if (scrollBottom < 200 && scrapedImagesHasMore && !scrapedImagesLoading) {
      fetchScrapedImages(true);
    }
  }, [scrapedImagesHasMore, scrapedImagesLoading, fetchScrapedImages]);

  const loadSettings = useCallback(async (reconcileWithDetection = false) => {
    try {
      const response = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/settings');
      if (response.data.success) {
        const settings = response.data.settings || {};
        if (settings.max_pages_default) {
          let desired = Number(settings.max_pages_default);
          if (reconcileWithDetection && detectedTotalPages) {
            desired = Math.min(desired, detectedTotalPages);
          }
          setMaxPages(desired);
        }
      }
    } catch (error) {
      console.error('[SearchPage] Error loading settings:', error);
    }
  }, [detectedTotalPages]);

  // Initialize component - fetch data when page opens (only once on mount)
  useEffect(() => {
    // Prevent duplicate initialization
    if (initRefLocal.current) {
      return;
    }
    
    // Prevent concurrent initializations
    if (searchPageInitInProgress) {
      return;
    }
    
    // Mark as initializing
    searchPageInitInProgress = true;
    initRefLocal.current = true;
    
    console.log('[SearchPage] Starting initialization...');
    
    const init = async () => {
      try {
        console.log('[SearchPage] Fetching data...');

        // Reset pagination state for scraped images only
        setScrapedImagesOffset(0);
        setScrapedImagesHasMore(true);

        // Fetch initial data in parallel
        const initPromises = [
          loadSettings(true),
          detectTotalPages(true),
          fetchStolenImages(),
          fetchScrapedImages(false),
          fetchSearchStatus(),
          fetchStatistics()
        ];
        await Promise.allSettled(initPromises);
        searchPageInitInProgress = false;
        initRefLocal.current = true;
        setInitDone(true);
        console.log('[SearchPage] Initialization complete');
      } catch (error) {
        console.error('[SearchPage] Initialization error:', error);
      } finally {
        searchPageInitInProgress = false;
      }
    };
    
    init();
    
    // Reset ref on unmount to allow re-initialization if component remounts
    return () => {
      console.log('[SearchPage] Component unmounting - state will be reset');
      initRefLocal.current = false;
    };
    // Empty deps - only run once on mount
    // If component remounts, this will run again and fetch fresh data
  }, []);
  
  // Debug: Log state changes to verify data is being set
  useEffect(() => {
    console.log('[SearchPage] State update - stolenImages:', stolenImages.length, 'scrapedImages:', scrapedImages.length, 'stats keys:', Object.keys(stats).length, 'detectedTotalPages:', detectedTotalPages);
    console.log('[SearchPage] State update - stolenImages array:', stolenImages);
  }, [stolenImages, scrapedImages, stats, detectedTotalPages]);

  // Refresh settings when page becomes visible (in case settings were changed in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('[SearchPage] Page became visible again - re-syncing status and stats');
        fetchSearchStatus();
        fetchStatistics();
        // Don't fetch stolen images again - already cached
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchSearchStatus, fetchStatistics]);



  const startSearch = async () => {
    if (searchStatus.is_running) {
      setShowBusyModal(true);
      return;
    }
    if (stolenImages.length === 0) {
      showNotification('No stolen images found to compare against', 'error');
      return;
    }

    try {
      setLoading(true);
      
      // Browse the fixed eBay.fr category without search query
      const searchQuery = ''; // Empty query means browsing the fixed category
      
      const response = await axios.post('https://outiloeuvresvolees.inouiagency.com/api/search', {
        query: searchQuery,
        max_pages: maxPages,
        filter_words: [],
        use_smart_filters: false,
        mode: 'auto' // Always use auto mode
      });

      if (response.data.success) {
        activeRunRef.current = true;
        showNotification('Search started successfully', 'success');
        // Update status from API response
        if (response.data.status) {
          const status = response.data.status;
          setSearchStatus(status);
          if (status.mode) {
            setCompareMode(status.mode);
          }
        } else {
          // Fallback to local state if API doesn't return status
          setSearchStatus(prevStatus => ({
            ...prevStatus,
            is_running: true,
            current_search: '',
            progress: 0,
            matches_found: 0,
            mode: 'auto', // Always use auto mode
          }));
        }
      } else {
        showNotification(response.data.error || 'Failed to start search', 'error');
      }
    } catch (error) {
      console.error('Error starting search:', error);
      showNotification('Error starting search', 'error');
    } finally {
      setLoading(false);
    }
  };

  const stopSearch = async () => {
    try {
      setLoading(true);
      const response = await axios.post('https://outiloeuvresvolees.inouiagency.com/api/search/stop');
      if (response.data.success) {
        activeRunRef.current = false;
        showNotification('Search stopped', 'warning');
        // Update status from API response
        if (response.data.status) {
          const status = response.data.status;
          setSearchStatus(status);
          // Check if matches were found before stopping
          if (status.matches_found > 0 && activeRunRef.current) {
            showNotification(
              `Search stopped. Found ${status.matches_found} matches.`, 
              'success'
            );
          }
        } else {
          // Fallback: fetch status if not in response
          await fetchSearchStatus();
        }
      }
    } catch (error) {
      console.error('Error stopping search:', error);
      showNotification('Error stopping search', 'error');
    } finally {
      setLoading(false);
    }
  };

  const compareBacklog = async () => {
    try {
      setLoading(true);
      const response = await axios.post('https://outiloeuvresvolees.inouiagency.com/api/compare-backlog', {
        mode: 'auto' // Always use auto mode
      });
      if (response.data.success) {
        activeRunRef.current = true;
        showNotification('Backlog comparison started', 'info');
        // Update status from API response
        if (response.data.status) {
          const status = response.data.status;
          setSearchStatus(status);
          if (status.mode) {
            setCompareMode(status.mode);
          }
        } else {
          // Fallback: fetch status if not in response
          await fetchSearchStatus();
        }
      } else {
        showNotification(response.data.error || 'Failed to start backlog comparison', 'error');
      }
    } catch (error) {
      console.error('Error starting backlog comparison:', error);
      showNotification('Error starting backlog comparison', 'error');
    } finally {
      setLoading(false);
    }
  };


  const BrowseForm = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Search className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800">Browse 20th Century & Contemporary Art</h2>
      </div>

      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            This will browse the eBay.fr "Art du XXe siècle contemporain" category and compare all listings with your stolen images.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Comparison Mode</label>
          <select
            value="auto"
            disabled
            className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm shadow-sm cursor-not-allowed"
          >
            <option value="auto">Auto (hybrid)</option>
          </select>
          <p className="mt-2 text-xs text-gray-500">
            Auto mode automatically selects the best comparison method (regular or camera) based on image characteristics.
          </p>
        </div>

        <div>
          <label htmlFor="max-pages" className="block text-sm font-medium text-gray-700 mb-2">
            Maximum Pages to Search
          </label>
          <select
            id="max-pages"
            value={maxPages}
            onChange={(e) => setMaxPages(Number(e.target.value))}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            disabled={searchStatus.is_running}
          >
            {(() => {
              const base = [1, 5, 10, 50, 100, 500, 1000, 2000, 3000, 5000, 10000];
              const cap = detectedTotalPages || 10000;
              const opts = base.filter(n => n <= cap);
              if (detectedTotalPages && !opts.includes(detectedTotalPages)) {
                opts.push(detectedTotalPages);
              }
              return opts.sort((a,b)=>a-b).map(num => (
                <option key={num} value={num}>{num}</option>
              ));
            })()}
          </select>
          {detectingTotal ? (
            <p className="mt-2 text-xs text-gray-500 flex items-center">
              <span className="inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></span>
              Detecting total pages on eBay...
            </p>
          ) : detectedTotalPages !== null && detectedTotalPages !== undefined ? (
            <p className="mt-2 text-xs text-gray-600">
              Detected total pages on eBay: <span className="font-medium">{detectedTotalPages}</span>
              {detectedTotalResults ? ` (${detectedTotalResults.toLocaleString()} results)` : ''}
            </p>
          ) : (
            <p className="mt-2 text-xs text-gray-400">Could not detect total pages at this time.</p>
          )}
        </div>

        <div className="flex space-x-4">
          <button
            onClick={searchStatus.is_running ? stopSearch : startSearch}
            className={`flex-1 flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
              searchStatus.is_running ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            disabled={loading || stolenImages.length === 0}
          >
            {searchStatus.is_running ? (
              <>
                <Square className="h-5 w-5 mr-2" />
                Stop Browsing
              </>
            ) : (
              <>
                <Play className="h-5 w-5 mr-2" />
                Start Browsing
              </>
            )}
          </button>

          {!searchStatus.is_running && (
            <button
              onClick={compareBacklog}
              className="flex-1 flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Compare Backlog Now
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const BusyModal = () => (
    <div className={`${searchStatus.is_running && showBusyModal ? '' : 'hidden'} fixed inset-0 z-50 flex items-center justify-center`}>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowBusyModal(false)}></div>
      <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Operation In Progress</h3>
        <p className="text-sm text-gray-600 mb-4">
          A comparison or scraping task is currently running. Please wait until it finishes before starting a new browse.
        </p>
        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={() => setShowBusyModal(false)}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
          >
            OK, I&#39;ll wait
          </button>
        </div>
      </div>
    </div>
  );

  const SearchProgress = () => {
    if (!searchStatus.is_running) return null;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-blue-600 animate-pulse" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900">Search in Progress</h3>
              <p className="text-blue-700">Searching for: {searchStatus.current_search}</p>
              <p className="text-xs text-blue-600 mt-1">
                {searchStatus.current_phase === 'scraping' ? 'Phase 1: Scraping eBay listings...' : 
                 searchStatus.current_phase === 'comparing' ? 'Phase 2: Comparing images...' : 
                 'Search completed'}
                {searchStatus.elapsed_seconds > 0 && (
                  <span className="ml-2">• {Math.floor(searchStatus.elapsed_seconds / 60)}m {searchStatus.elapsed_seconds % 60}s</span>
                )}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-blue-900">{searchStatus.progress.toFixed(1)}%</p>
            <p className="text-sm text-blue-700">Complete</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="w-full bg-blue-200 rounded-full h-3">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${searchStatus.progress}%` }}
              ></div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-blue-700">Images Processed:</span>
              <span className="font-semibold text-blue-900">{searchStatus.total_images_processed}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-blue-700">Matches Found:</span>
              <span className="font-semibold text-blue-900">{searchStatus.matches_found}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StolenImagesOverview = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Image className="h-5 w-5 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800">Monitoring Overview</h2>
        </div>
        <button
          onClick={fetchStolenImages}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {stolenImages.length > 0 ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-800">
                <strong>{stolenImages.length} images</strong> are being monitored for matches.
              </p>
            </div>
          </div>
          
          <div 
            className="overflow-y-auto pr-2" 
            style={{maxHeight: '360px', scrollbarWidth: 'thin'}}
          >
            <div className="grid grid-cols-3 gap-3">
              {stolenImages.map((image, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <img 
                    src={`https://outiloeuvresvolees.inouiagency.com/api/stolen-images/${image.filename}`}
                    alt={image.filename}
                    className="w-full h-32 object-cover rounded-lg mb-2"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22%3E%3Crect fill=%22%23ddd%22 width=%22200%22 height=%22200%22/%3E%3Ctext fill=%22%23999%22 font-family=%22sans-serif%22 font-size=%2214%22 x=%2210%22 y=%22110%22%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {image.filename}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(image.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ))}
            </div>
            {stolenImagesLoading && (
              <div className="text-center py-4">
                <RefreshCw className="h-5 w-5 animate-spin text-gray-400 mx-auto" />
                <p className="text-sm text-gray-500 mt-2">Loading more images...</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Stolen Images Found</h3>
          <p className="text-gray-500 mb-4">
            Add images to the Stolen-pictures folder to start monitoring.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">How to add stolen images:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Place your stolen images in the "Stolen-pictures" folder</li>
                  <li>Supported formats: JPG, PNG, GIF, BMP, TIFF, WebP</li>
                  <li>Refresh this page to see your images</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Debug component to verify data is available
  const DebugInfo = () => {
    if (process.env.NODE_ENV === 'development') {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs mb-4">
          <strong>Debug:</strong> stolenImages={stolenImages.length}, scrapedImages={scrapedImages.length}, 
          stats={Object.keys(stats).length > 0 ? 'yes' : 'no'}, totalPages={detectedTotalPages || 'none'}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8">
      <BusyModal />
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Search for Stolen Images
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Search eBay listings to find potential matches with your stolen images. 
          The system will automatically compare and alert you to any similarities.
        </p>
      </div>
      
      <DebugInfo />



      {/* Search Progress (only shown when running) */}
      <SearchProgress />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <BrowseForm />
          
          {/* Scraping Report */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800">Scraping Report</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600">Total Images Scraped</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_scraped ?? stats.total_scraped_images ?? 0}</p>
                <p className="text-xs text-gray-500">Last 7 days: {stats.recent_scraped ?? 0}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600">Total Searches</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_searches ?? 0}</p>
                <p className="text-xs text-gray-500">Active filters: {stats.active_filters ?? 0}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">Stats aggregate across runs. Use Refresh Stats to update.</p>
          </div>

          {/* Information Message */}
          <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4">
            <p className="text-sm">
              Any feedback? Let me fix and update this system as you want.<br/>
              Whatsapp number: +1 (252) 867-7248
            </p>
          </div>
        </div>
        
        <div className="space-y-6">
          <StolenImagesOverview />
          
          {/* Scraped Images List - Always visible under Monitoring Overview */}
          {scrapedImages.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" style={{height: 'fit-content', maxHeight: '600px', display: 'flex', flexDirection: 'column'}}>
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-800">Recent Scraped Images</h2>
                <span className="text-sm text-gray-500">{scrapedImages.length} images</span>
              </div>
              <div 
                ref={scrapedImagesScrollRef}
                className="overflow-y-auto pr-2 flex-1" 
                style={{scrollbarWidth: 'thin'}}
                onScroll={handleScrapedImagesScroll}
              >
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {scrapedImages.map((img, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <img 
                        src={img.url} 
                        alt={img.title || 'Scraped image'} 
                        className="w-full h-32 object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22%3E%3Crect fill=%22%23ddd%22 width=%22200%22 height=%22200%22/%3E%3Ctext fill=%22%23999%22 font-family=%22sans-serif%22 font-size=%2214%22 x=%2210%22 y=%22110%22%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      <div className="p-2">
                        <a 
                          href={img.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="block text-center text-blue-600 hover:text-blue-700 text-xs font-medium hover:underline truncate"
                        >
                          View Listing
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                {scrapedImagesLoading && (
                  <div className="text-center py-4">
                    <RefreshCw className="h-5 w-5 animate-spin text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-500 mt-2">Loading more images...</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Search Tips</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div className="flex items-start space-x-2">
            <Clock className="h-4 w-4 mt-0.5 text-blue-500" />
            <div>
              <p className="font-medium text-gray-800">Be Patient</p>
              <p>Searches can take several minutes depending on the number of pages.</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <Search className="h-4 w-4 mt-0.5 text-green-500" />
            <div>
              <p className="font-medium text-gray-800">Use Specific Terms</p>
              <p>Use specific keywords related to your stolen items for better results.</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 mt-0.5 text-orange-500" />
            <div>
              <p className="font-medium text-gray-800">Check Matches</p>
              <p>Review all matches carefully as the system may find similar but different items.</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <RefreshCw className="h-4 w-4 mt-0.5 text-purple-500" />
            <div>
              <p className="font-medium text-gray-800">Regular Monitoring</p>
              <p>Run searches regularly as new listings are added to eBay constantly.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
